import { useMutation } from '@tanstack/react-query';
import { ComponentProps } from 'preact';
import { useEffect, useId, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { emailRegex, passwordRegex } from '@/helpers';
import { patchUserMutationFn, postResetPasswordMutationFn, postUserPasswordMutationFn } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { updateUserProperty } from '@/redux/slices/user/actions';
import { ErrorsMap, RequestError, toErrorsMap } from '@/shared/fetch';

import {
  ButtonContainer,
  Content,
  FieldContainer,
  FieldError,
  FieldInput,
  FieldLabel,
  FieldPasswordInput,
  Header,
  HeaderTitle,
  PrimaryButton,
  Section,
} from './Section';

type FormErrors = ErrorsMap<{ currentPassword?: unknown; newPassword?: unknown; passwordConf?: unknown }>;

const NoPasswordSection = () => {
  const email = useSelector((state) => state.user.email);
  const [isSent, setIsSent] = useState(false);
  const { mutate, isPending } = useMutation({
    mutationFn: postResetPasswordMutationFn,
    onSuccess: () => {
      setIsSent(true);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const requestSetPasswordLink = () => {
    if (email) {
      mutate(email);
    }
  };

  useEffect(() => {
    if (!isSent) {
      return undefined;
    }
    const id = setTimeout(() => {
      setIsSent(false);
    }, 5000);
    return () => clearTimeout(id);
  }, [isSent]);

  return (
    <div className="mt-10 desktop:mt-20">
      <p className="preflight preflight-p mb-6 text-base text-grey-2">
        You can always access your account with magic email links we send you. If you prefer using a password too, we’ll send
        you a link to create one.
      </p>
      <div className="flex flex-col gap-2 pb-8 desktop:items-end">
        <PrimaryButton onClick={requestSetPasswordLink} disabled={isPending}>
          {isPending ? 'Sending...' : 'Send Link'}
        </PrimaryButton>
        {isSent && (
          <p className="preflight preflight-p text-center text-sm text-primary-green">Link sent, check your inbox.</p>
        )}
      </div>
    </div>
  );
};

const AccountSection = () => {
  const id = useId();
  const dispatch = useDispatch();
  const isPasswordLocked = useSelector((state) => state.user.isPasswordLocked);
  const existingEmail = useSelector((state) => state.user.email);
  const [email, setEmail] = useState(existingEmail ?? '');
  const [emailError, setEmailError] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  const {
    mutate: updateUser,
    reset: updateUserReset,
    isPending: isUpdateUserPending,
    isError: isUpdateUserError,
    isSuccess: isUpdateUserSuccess,
  } = useMutation({
    mutationFn: patchUserMutationFn,
    onSuccess: ({ email: newEmail }) => {
      dispatch(updateUserProperty({ email: newEmail }));
    },
    onError: (error) => {
      console.error(error);
      setEmailError(
        (toErrorsMap<{ email?: unknown }>(error)?.email?.join(' ') ?? '') || 'Something went wrong, please try again!',
      );
    },
  });

  const {
    mutate: changePassword,
    reset: changePasswordReset,
    isPending: isChangePasswordPending,
    isError: isChangePasswordError,
    isSuccess: isChangePasswordSuccess,
  } = useMutation({
    mutationFn: postUserPasswordMutationFn,
    onError: (error) => {
      console.error(error);
      if (error instanceof RequestError) {
        if (error.body.passwordError) {
          setErrors({
            currentPassword: [error.body.passwordError],
          });
        } else {
          setErrors({
            '*': [error.body.error || 'Something went wrong, please try again!'],
          });
        }
      } else {
        setErrors({
          '*': ['Something went wrong, please try again!'],
        });
      }
    },
  });

  const isPending = isUpdateUserPending || isChangePasswordPending;
  const isSuccess = (isUpdateUserSuccess || isChangePasswordSuccess) && !isUpdateUserError && !isChangePasswordError;

  const onSubmit: ComponentProps<'form'>['onSubmit'] = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const currentPassword = formData.get('currentPassword') as string;
    const newPassword = formData.get('newPassword') as string;
    const passwordConf = formData.get('passwordConf') as string;
    const newErrors: typeof errors = {};
    let newEmailError = '';

    if (!email) {
      newEmailError = 'Email is required';
    } else if (!new RegExp(emailRegex).test(email)) {
      newEmailError = 'Please enter a valid email';
    }

    if (newPassword && !new RegExp(passwordRegex).test(newPassword)) {
      newErrors.newPassword = ['Password must be at least 8 characters long and contain at least 1 number and 1 character'];
    }

    if (newPassword && !currentPassword) {
      newErrors.currentPassword = ['Current password is required for password change'];
    }

    if (newPassword && !passwordConf) {
      newErrors.passwordConf = ['Password confirmation is required for password change'];
    }

    if (newPassword && passwordConf && newPassword !== passwordConf) {
      newErrors.passwordConf = ['Passwords do not match'];
    }

    setErrors(newErrors);
    setEmailError(newEmailError);

    if (Object.keys(newErrors).length > 0 || newEmailError) {
      changePasswordReset();
      updateUserReset();
      return;
    }

    if (email !== existingEmail) {
      updateUser({ email });
    } else {
      updateUserReset();
    }

    if (newPassword) {
      changePassword({ currentPassword, newPassword, passwordConf });
    } else {
      changePasswordReset();
    }
  };

  return (
    <Section>
      <Header>
        <HeaderTitle>Account</HeaderTitle>
      </Header>
      <Content>
        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-4 desktop:grid-cols-2 desktop:gap-6">
            <FieldContainer>
              <FieldLabel htmlFor={`${id}-email`}>Email</FieldLabel>
              <FieldInput
                id={`${id}-email`}
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                data-error={emailError ? 'true' : undefined}
              />
              {emailError && <FieldError>{emailError}</FieldError>}
              <p className="preflight preflight-p mt-1.5 text-xs font-medium text-stone-500">
                We’ll send event reminders to this address when you click “Remind me”
              </p>
            </FieldContainer>
            {!isPasswordLocked && (
              <>
                <FieldContainer>
                  <FieldLabel htmlFor={`${id}-currentPassword`}>Current Password</FieldLabel>
                  <FieldPasswordInput
                    id={`${id}-currentPassword`}
                    name="currentPassword"
                    autoComplete="current-password"
                    data-error={errors.currentPassword ? 'true' : undefined}
                  />
                  {errors.currentPassword && <FieldError>{errors.currentPassword.join(' ')}</FieldError>}
                </FieldContainer>
                <FieldContainer>
                  <FieldLabel htmlFor={`${id}-newPassword`}>New Password</FieldLabel>
                  <FieldPasswordInput
                    id={`${id}-newPassword`}
                    name="newPassword"
                    autoComplete="new-password"
                    data-error={errors.newPassword ? 'true' : undefined}
                  />
                  {errors.newPassword && <FieldError>{errors.newPassword.join(' ')}</FieldError>}
                </FieldContainer>
                <FieldContainer>
                  <FieldLabel htmlFor={`${id}-passwordConf`}>Confirm Password</FieldLabel>
                  <FieldPasswordInput
                    id={`${id}-passwordConf`}
                    name="passwordConf"
                    autoComplete="new-password"
                    data-error={errors.passwordConf ? 'true' : undefined}
                  />
                  {errors.passwordConf && <FieldError>{errors.passwordConf.join(' ')}</FieldError>}
                </FieldContainer>
              </>
            )}
          </div>
          <ButtonContainer className="mt-6">
            {errors['*'] && <FieldError className="desktop:self-end">{errors['*'].join(' ')}</FieldError>}
            <PrimaryButton type="submit" disabled={isPending}>
              {isPending ? 'Updating...' : isSuccess ? 'Updated ✔' : 'Update'}
            </PrimaryButton>
          </ButtonContainer>
        </form>
        {isPasswordLocked && <NoPasswordSection />}
      </Content>
    </Section>
  );
};

export default AccountSection;
